import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";
import { ListItemValueChange } from "../list-item/list-item";

@Component({
    tag: "uic-check-list-item",
    styleUrl: "check-list-item.scss"
})
export class CheckListItem implements ComponentInterface {
    @Element() el: HTMLUicCheckListItemElement;

    /**
     * If set to `true`, the list item is considered selected.
     */
    @Prop({ reflect: true }) readonly selected: boolean = false;

    /**
     * If set to `true`, the user cannot interact with this element.
     */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /**
     * The value.
     */
    @Prop() readonly value: string;

    /**
     * The optional icon.
     */
    @Prop() readonly icon?: string;

    /**
     * If set to `true`, the item acts as controller for other items.
     */
    @Prop({ attribute: "uic-controller" }) readonly controller = false;

    /**
     * The name of the group.
     */
    @Prop() readonly group?: string;

    /**
     * @internal Sets the checkbox to `mixed`. Only applicable when the element is a controller.
     */
    @Prop() readonly mixed: boolean = false;

    /**
     * If set to `true`, enables an additional slot for secondary content.
     */
    @Prop({ reflect: true }) readonly multiline: boolean = false;

    /**
     * @internal Event to request selection.
     */
    @Event({ eventName: "uicSelect" })
    select: EventEmitter;

    /**
     * Notifies containing list about item being added.
     *
     * @internal
     */
    @Event({ eventName: "uicItemAdded" })
    added: EventEmitter<string>;

    /**
     * Notifies containing list about item being updated.
     *
     * @internal
     */
    @Event({ eventName: "uicItemUpdated" })
    updated: EventEmitter<ListItemValueChange>;

    /**
     * This flag is essential to control uicItemAdded emitted only after item
     * was rendered properly at least once.
     */
    private notifiedOnAddedBefore = false;

    private onChange(event: Event) {
        event.stopPropagation();
        this.select.emit();
    }

    public connectedCallback() {
        if (this.notifiedOnAddedBefore) {
            this.added.emit(this.value);
        }
    }

    public componentDidLoad() {
        this.added.emit(this.value);
        this.notifiedOnAddedBefore = true;
    }

    @Watch("value")
    public onValueChanged(current: string, previous: string): void {
        if (this.notifiedOnAddedBefore) {
            this.updated.emit({ previous, current });
        }
    }

    render() {
        return (
            <Host uic-item tabindex={this.selected && !this.disabled ? 0 : -1}>
                <uic-checkbox
                    checked={this.selected}
                    disabled={this.disabled}
                    mixed={this.mixed}
                    onUicChange={(e) => this.onChange(e)}
                >
                    <slot name='icon'>{this.icon && <uic-icon class='uic-check-list-item__icon' name={this.icon}></uic-icon>}</slot>
                    <div class='uic-check-list-item__content-default'>
                        <slot></slot>
                    </div>
                    <div class='uic-check-list-item__content-default uic-check-list-item__content-primary'>
                        <slot name='primary'></slot>
                    </div>
                </uic-checkbox>
            </Host>
        );
    }
}
